import { Accordion, Flex, type BoxProps } from '@mantine/core'
import { clsx } from 'clsx'
import React, { type PropsWithChildren, useEffect, useState } from 'react'

import { findReactChildrenByDisplayName } from '@flecto/utils/src/Window/findReactChildrenByDisplayName'

import useMediaQuery from '../../lib/utils/useMediaQuery'
import { CollapsibleCardContent } from './Content'
import { CollapsibleCardDescription } from './Description'
import { CollapsibleCardIcon } from './Icon'
import { CollapsibleCardRightSection } from './RightSection'
import classes from './styles.module.css'
import { CollapsibleCardTitle } from './Title'

export type CollapsibleCardProps = PropsWithChildren<BoxProps> & {
  opened?: boolean
  onChangeOpen?: (opened: boolean) => void
  preventOpen?: boolean
}

export function CollapsibleCard({
  children,
  opened,
  onChangeOpen,
  preventOpen = false,
  ...props
}: CollapsibleCardProps) {
  const { isMobile } = useMediaQuery()
  const [value, setValue] = useState<string | null>(null)
  const [title, description, icon, rightSection, content] = [
    'CollapsibleCard.Title',
    'CollapsibleCard.Description',
    'CollapsibleCard.Icon',
    'CollapsibleCard.RightSection',
    'CollapsibleCard.Content',
  ].map((component) => findReactChildrenByDisplayName(children, component))

  useEffect(() => {
    if (opened) {
      setValue('default')
    } else {
      setValue(null)
    }
  }, [opened])

  const handleOnChangeOpen = (value: string | null) => {
    setValue(value)
    onChangeOpen?.(Boolean(value))
  }

  return (
    <Accordion
      chevronPosition='right'
      variant='contained'
      multiple={false}
      w='100%'
      value={value}
      onChange={handleOnChangeOpen}
      chevron={preventOpen ? false : <Accordion.Chevron />}
      style={{
        pointerEvents: preventOpen ? 'none' : 'all',
      }}
      className={classes.content}
      {...props}
    >
      <Accordion.Item value='default' className={classes.container} px='lg' py='sm'>
        <Accordion.Control p={0}>
          <Flex
            justify='space-between'
            align='center'
            w='100%'
            className={clsx({
              [classes.cardCollumnMobile]: isMobile,
            })}
          >
            <Flex gap={16} align='center'>
              {icon}
              <Flex direction='column'>
                {title}
                {description}
              </Flex>
            </Flex>
            <Flex pl={isMobile ? 'lg' : ''}>{rightSection}</Flex>
          </Flex>
        </Accordion.Control>
        <Accordion.Panel>{content}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  )
}

CollapsibleCard.Title = CollapsibleCardTitle
CollapsibleCard.Description = CollapsibleCardDescription
CollapsibleCard.Icon = CollapsibleCardIcon
CollapsibleCard.RightSection = CollapsibleCardRightSection
CollapsibleCard.Content = CollapsibleCardContent
